import React from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  TextField,
  Card,
  CardContent,
  Grid,
  IconButton,
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import SearchIcon from '@mui/icons-material/Search';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0052CC 0%, #0747A6 100%)',
  padding: theme.spacing(8, 0),
  marginTop: theme.spacing(8),
  color: 'white',
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  maxWidth: 800,
  margin: '0 auto',
  padding: theme.spacing(0, 2),
}));

const CategoryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  textDecoration: 'none',
  display: 'block',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const VipJobCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: `2px solid ${theme.palette.primary.main}`,
  textDecoration: 'none',
  display: 'block',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const CompanyCard = styled(Card)(({ theme }) => ({
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

// Mock data
const categories = [
  'IT & Software', 'Finance', 'Marketing', 'Sales', 'HR', 'Design',
  'Engineering', 'Healthcare', 'Education', 'Legal'
];

const vipJobs = [
  { id: 1, title: 'Senior Frontend Developer', company: 'Tech Corp', salary: '$80k-$120k' },
  { id: 2, title: 'Product Manager', company: 'Innovation Inc', salary: '$90k-$130k' },
  { id: 3, title: 'UX Designer', company: 'Design Studio', salary: '$70k-$100k' },
];

const vipCompanies = [
  { id: 1, name: 'Google' },
  { id: 2, name: 'Microsoft' },
  { id: 3, name: 'Apple' },
  { id: 4, name: 'Amazon' },
  { id: 5, name: 'Meta' },
  { id: 6, name: 'Netflix' },
  { id: 7, name: 'Adobe' },
  { id: 8, name: 'Salesforce' },
  { id: 9, name: 'Oracle' },
  { id: 10, name: 'IBM' },
];

const latestJobs = [
  { id: 4, title: 'Junior Developer', company: 'StartUp Co', salary: '$50k-$70k' },
  { id: 5, title: 'Marketing Manager', company: 'Brand Agency', salary: '$60k-$90k' },
  { id: 6, title: 'Data Analyst', company: 'Data Corp', salary: '$55k-$85k' },
];

function HomePage() {
  return (
    <Box>
      {/* Header */}
      <StyledAppBar>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main' }}>
            JobsPortal
          </Typography>
          <Button color="primary">Sign In</Button>
          <Button variant="contained" color="primary">Post a Job</Button>
        </Toolbar>
      </StyledAppBar>

      {/* Search Section */}
      <SearchContainer>
        <Container>
          <Typography variant="h4" align="center" gutterBottom>
            Find Your Dream Job
          </Typography>
          <SearchBox>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Job title, keywords, or company"
              sx={{ bgcolor: 'white', borderRadius: 1 }}
            />
            <Button
              component={Link}
              to="/jobs"
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </SearchBox>
        </Container>
      </SearchContainer>

      {/* Categories */}
      <Container sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          Popular Categories
        </Typography>
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={6} sm={4} md={2} key={category}>
              <CategoryCard component={Link} to={`/jobs?category=${encodeURIComponent(category)}`}>
                <CardContent>
                  <Typography align="center" color="textPrimary">{category}</Typography>
                </CardContent>
              </CategoryCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* VIP Jobs */}
      <Container sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          VIP Jobs
        </Typography>
        <Grid container spacing={3}>
          {vipJobs.map((job) => (
            <Grid item xs={12} md={4} key={job.id}>
              <VipJobCard component={Link} to={`/jobs/${job.id}`}>
                <CardContent>
                  <Typography variant="h6" color="textPrimary">{job.title}</Typography>
                  <Typography color="textSecondary">{job.company}</Typography>
                  <Typography color="primary">{job.salary}</Typography>
                </CardContent>
              </VipJobCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* VIP Companies Carousel */}
      <Container sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          Top Companies
        </Typography>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={30}
          slidesPerView={4}
          navigation
          pagination={{ clickable: true }}
          breakpoints={{
            320: { slidesPerView: 1 },
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
          }}
        >
          {vipCompanies.map((company) => (
            <SwiperSlide key={company.id}>
              <CompanyCard component={Link} to={`/companies/${company.id}`}>
                <Typography variant="h6" color="textPrimary">{company.name}</Typography>
              </CompanyCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>

      {/* Latest Jobs */}
      <Container sx={{ mt: 6, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Latest Jobs
          </Typography>
          <Button
            component={Link}
            to="/jobs"
            color="primary"
            endIcon={<ArrowForwardIcon />}
          >
            View All Jobs
          </Button>
        </Box>
        <Grid container spacing={3}>
          {latestJobs.map((job) => (
            <Grid item xs={12} md={4} key={job.id}>
              <Card 
                component={Link} 
                to={`/jobs/${job.id}`} 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="textPrimary">{job.title}</Typography>
                  <Typography color="textSecondary">{job.company}</Typography>
                  <Typography color="primary">{job.salary}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default HomePage; 